import crypto from "node:crypto";
import http from "node:http";
import readline from "node:readline";
import open from "open";
import {
  saveAuth,
  generatePkce,
  OAUTH_AUTHORIZE_URL,
  OAUTH_TOKEN_URL,
  OAUTH_CLIENT_ID,
} from "../lib/auth.js";
import { api } from "../lib/api.js";

function findPort(): Promise<number> {
  return new Promise((resolve, reject) => {
    const server = http.createServer();
    server.listen(0, () => {
      const addr = server.address();
      if (addr && typeof addr === "object") {
        const port = addr.port;
        server.close(() => resolve(port));
      } else {
        reject(new Error("Could not find available port"));
      }
    });
  });
}

export async function loginCommand(): Promise<void> {
  const { codeVerifier: verifier, codeChallenge: challenge } = generatePkce();
  const state = crypto.randomBytes(16).toString("hex");
  const port = 19823;
  const redirectUri = `http://localhost:${port}/callback`;

  const authUrl = new URL(OAUTH_AUTHORIZE_URL);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("client_id", OAUTH_CLIENT_ID);
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("code_challenge", challenge);
  authUrl.searchParams.set("code_challenge_method", "S256");
  authUrl.searchParams.set("scope", "openid email");
  authUrl.searchParams.set("state", state);

  const result = await new Promise<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }>((resolve, reject) => {
    const timeout = setTimeout(() => {
      server.close();
      reject(new Error("Timed out waiting for authorization."));
    }, 5 * 60 * 1000);

    const server = http.createServer(async (req, res) => {
      const url = new URL(req.url!, `http://localhost:${port}`);
      if (url.pathname !== "/callback") {
        res.writeHead(404);
        res.end("Not found");
        return;
      }

      const code = url.searchParams.get("code");
      const returnedState = url.searchParams.get("state");
      const error = url.searchParams.get("error");

      if (error) {
        res.writeHead(200, { "Content-Type": "text/html" });
        res.end(
          "<html><body><h2>Authorization denied.</h2><p>You can close this window.</p></body></html>"
        );
        clearTimeout(timeout);
        server.close();
        reject(new Error(`Authorization denied: ${error}`));
        return;
      }

      if (!code || returnedState !== state) {
        res.writeHead(400, { "Content-Type": "text/html" });
        res.end(
          "<html><body><h2>Invalid callback.</h2></body></html>"
        );
        return;
      }

      // Exchange code for tokens
      try {
        const tokenRes = await fetch(OAUTH_TOKEN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            grant_type: "authorization_code",
            code,
            client_id: OAUTH_CLIENT_ID,
            redirect_uri: redirectUri,
            code_verifier: verifier,
          }),
        });

        if (!tokenRes.ok) {
          const err = await tokenRes.text();
          throw new Error(`Token exchange failed: ${err}`);
        }

        const tokens = (await tokenRes.json()) as {
          access_token: string;
          refresh_token: string;
          expires_in: number;
          token_type: string;
        };

        res.writeHead(200, { "Content-Type": "text/html" });
        res.end(
          "<html><body><h2>Logged in!</h2><p>You can close this window and return to the terminal.</p></body></html>"
        );

        clearTimeout(timeout);
        server.close();
        resolve(tokens);
      } catch (err) {
        res.writeHead(500, { "Content-Type": "text/html" });
        res.end(
          "<html><body><h2>Login failed.</h2></body></html>"
        );
        clearTimeout(timeout);
        server.close();
        reject(err);
      }
    });

    server.listen(port, () => {
      console.log("Opening browser to authorize...");
      console.log("\nBrowser didn't open? Use the url below to sign in:\n");
      console.log(`  ${authUrl.toString()}\n`);
      open(authUrl.toString()).catch(() => {
        // No browser available (SSH/headless) — the printed URL is the fallback
      });
      console.log("Waiting for authorization...");
    });
  });

  // Decode JWT to get email
  let email = "unknown";
  try {
    const payload = JSON.parse(
      Buffer.from(result.access_token.split(".")[1], "base64").toString()
    );
    email = payload.email || "unknown";
  } catch {
    // ignore decode errors
  }

  const expiresAt = Math.floor(Date.now() / 1000) + result.expires_in;

  // Save tokens first (needed for API calls)
  saveAuth({
    access_token: result.access_token,
    refresh_token: result.refresh_token,
    expires_at: expiresAt,
  });

  // Fetch user's orgs and prompt to select
  let orgId: string | undefined;
  try {
    const orgsRes = await api.get("/v1/orgs");
    if (orgsRes.ok) {
      const { orgs } = (await orgsRes.json()) as {
        orgs: Array<{ id: string; name: string; slug: string; role: string }>;
      };

      if (orgs.length === 1) {
        orgId = orgs[0].id;
        console.log(`Organization: ${orgs[0].name}`);
      } else if (orgs.length > 1) {
        console.log("\nSelect an organization:\n");
        orgs.forEach((org, i) => {
          console.log(`  ${i + 1}) ${org.name} (${org.role})`);
        });
        console.log();

        const rl = readline.createInterface({
          input: process.stdin,
          output: process.stdout,
        });

        const choice = await new Promise<string>((resolve) => {
          rl.question("Enter number: ", (answer) => {
            rl.close();
            resolve(answer.trim());
          });
        });

        const index = parseInt(choice, 10) - 1;
        if (index >= 0 && index < orgs.length) {
          orgId = orgs[index].id;
          console.log(`Selected: ${orgs[index].name}`);
        } else {
          orgId = orgs[0].id;
          console.log(`Defaulting to: ${orgs[0].name}`);
        }
      }
    }
  } catch {
    // If org fetch fails, continue without org_id
  }

  // Save again with org_id
  saveAuth({
    access_token: result.access_token,
    refresh_token: result.refresh_token,
    expires_at: expiresAt,
    org_id: orgId,
  });

  console.log(`Logged in as ${email}`);
}
